import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import { SAUDI_ADDITIONAL_TAXES } from './utils/saudiTaxConfig';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator, Calendar, Send, CheckCircle, AlertTriangle, Clock,
  RefreshCw, Eye, Banknote, FileSpreadsheet, Download, X,
  ChevronUp, ChevronDown, AlertCircle, LayoutGrid, BarChart3,
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { getAdditionalTaxes, calculateAdditionalTax } from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import { logger } from '../../shared/utils/logger';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';

const DEFAULT_TAXES_DATA = SAUDI_ADDITIONAL_TAXES;

const mapApiStatus = (status) => {
  if (status === 'paid' || status === 'declared') return 'completed';
  if (status === 'calculated') return 'in_progress';
  return 'pending';
};

const mapApiTaxRow = (row) => ({
  id: row.id,
  code: (row.tax_code || '').toUpperCase(),
  category: ['ira', 'vf', 'tap', 'foncier'].includes(row.tax_code) ? row.tax_code : 'autres',
  nom: row.tax_name || row.tax_code,
  type: row.tax_name || row.tax_code || '',
  taux: row.rate ? `${(row.rate * 100).toFixed(1)}%` : '—',
  deadline: row.due_date ? new Date(row.due_date).toLocaleDateString(getBrandLocale('ar')) : '—',
  montantDu: row.amount || 0,
  baseImposable: row.base_amount || 0,
  status: mapApiStatus(row.status),
  reductions: [],
  details: row.calculation_details ? (typeof row.calculation_details === 'string' ? JSON.parse(row.calculation_details) : row.calculation_details) : {},
});

const mergeTaxesData = (prev, apiRows) => {
  if (!apiRows?.length) return prev;
  const mapped = apiRows.map(mapApiTaxRow);
  const calendar = mapped.map((t) => ({
    date: t.deadline,
    taxe: t.nom,
    montant: t.montantDu,
    status: t.status === 'completed' ? 'completed' : t.status === 'pending' ? 'pending' : 'urgent',
    taxId: t.id,
  }));
  const totalAnnuel = mapped.reduce((s, t) => s + t.montantDu, 0);
  const verse = mapped.filter((t) => t.status === 'completed').reduce((s, t) => s + t.montantDu, 0);
  const restantDu = totalAnnuel - verse;
  const pending = mapped.filter((t) => t.status !== 'completed');
  const next = pending.sort((a, b) => a.deadline.localeCompare(b.deadline))[0];
  return {
    ...prev,
    taxes: mapped,
    calendar: calendar.length ? calendar : prev.calendar,
    summary: { totalAnnuel, verse, restantDu },
    overview: {
      ...prev.overview,
      totalTaxes: mapped.length,
      completedTaxes: mapped.filter((t) => t.status === 'completed').length,
      pendingAmount: restantDu,
      nextDeadline: next?.deadline || prev.overview.nextDeadline,
    },
  };
};

const formatAmount = (n) => formatBrandMoney(n);

const EmptyTabState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-16 bg-white/5 rounded-xl border-2 border-dashed border-white/10">
    <Icon className="w-12 h-12 mx-auto mb-4 text-white/30" />
    <p className="text-white/80 font-medium">{title}</p>
    <p className="text-white/50 text-sm mt-2 max-w-md mx-auto">{description}</p>
  </div>
);

const CALENDAR_LIST_COLUMNS = [
  { key: 'date', labelKey: 'col_date' },
  { key: 'tax', labelKey: 'col_tax' },
  { key: 'amount', labelKey: 'col_amount' },
  { key: 'status', labelKey: 'col_status' },
];

const TaxesSupplementairesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.taxesSupplementairesPage.${key}`, opts);

  const getStatusLabel = useCallback((status) => {
    const map = {
      completed: p('status_completed'),
      in_progress: p('status_in_progress'),
      pending: p('status_pending'),
    };
    return map[status] || status;
  }, [t]);

  const calendarStatusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 'completed', label: p('cal_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'urgent', label: p('cal_urgent'), icon: AlertTriangle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    { value: 'pending', label: p('cal_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
    { value: 'future', label: p('cal_future'), icon: Calendar, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  ], p), [t]);

  const handleCalendarStatusChange = (recordId, newStatus) => {
    setCalendarStatusOverrides((prev) => ({ ...prev, [recordId]: newStatus }));
  };

  const mainTabs = useMemo(() => [
    { id: 'taxes', label: p('tab_taxes'), icon: LayoutGrid },
    { id: 'calendrier', label: p('tab_calendrier'), icon: Calendar },
    { id: 'synthese', label: p('tab_synthese'), icon: BarChart3 },
  ], [t]);

  const taxFilters = useMemo(() => [
    { id: 'all', label: p('filter_all') },
    { id: 'ira', label: p('filter_ira') },
    { id: 'vf', label: p('filter_vf') },
    { id: 'tap', label: p('filter_tap') },
    { id: 'foncier', label: p('filter_foncier') },
    { id: 'autres', label: p('filter_autres') },
  ], [t]);

  const iraCategories = useMemo(() => [
    { value: 'cadre', label: p('ira_cat_cadre') },
    { value: 'maitrise', label: p('ira_cat_maitrise') },
    { value: 'employe', label: p('ira_cat_employe') },
  ], [t]);

  const reportTypes = useMemo(() => [
    { value: 'all', label: p('report_all') },
    { value: 'ira', label: p('report_ira') },
    { value: 'vf', label: p('report_vf') },
    { value: 'tap', label: p('report_tap') },
    { value: 'foncier', label: p('report_foncier') },
  ], [t]);

  const monthNames = useMemo(() => [
    p('month_january'), p('month_february'), p('month_march'), p('month_april'),
    p('month_may'), p('month_june'), p('month_july'), p('month_august'),
    p('month_september'), p('month_october'), p('month_november'), p('month_december'),
  ], [t]);

  const formatAmountLocale = useCallback(
    (n) => formatBrandMoney(n),
    []
  );

  const formatDa = useCallback(
    (n) => formatBrandMoney(n),
    []
  );

  const [taxesData, setTaxesData] = useState(DEFAULT_TAXES_DATA);
  const [activeTab, setActiveTab] = useState('taxes');
  const [taxFilter, setTaxFilter] = useState('all');
  const [selectedTax, setSelectedTax] = useState(null);
  const [showTaxDetailModal, setShowTaxDetailModal] = useState(false);
  const [showEcheancier, setShowEcheancier] = useState(false);
  const [showTeledeclarer, setShowTeledeclarer] = useState(false);
  const [showCalculIRA, setShowCalculIRA] = useState(false);
  const [showRapportFiscal, setShowRapportFiscal] = useState(false);
  const [showTeledeclarationsZATCA, setShowTeledeclarationsZATCA] = useState(false);
  const [showPlanningFiscal, setShowPlanningFiscal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [iraSalaire, setIraSalaire] = useState('');
  const [iraCategorie, setIraCategorie] = useState('cadre');
  const [calendarStatusOverrides, setCalendarStatusOverrides] = useState({});
  const [iraResult, setIraResult] = useState(null);
  const [iraCalculating, setIraCalculating] = useState(false);
  const [reportType, setReportType] = useState('all');
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');

  const fiscalMonths = useMemo(() => ([
    { key: 'month_january', num: 1 },
    { key: 'month_february', num: 2 },
    { key: 'month_march', num: 3 },
    { key: 'month_april', num: 4 },
    { key: 'month_may', num: 5 },
    { key: 'month_june', num: 6 },
    { key: 'month_july', num: 7 },
    { key: 'month_august', num: 8 },
    { key: 'month_september', num: 9 },
    { key: 'month_october', num: 10 },
    { key: 'month_november', num: 11 },
    { key: 'month_december', num: 12 },
  ]), []);

  const filteredTaxes = useMemo(() => {
    const list = taxesData.taxes || [];
    if (taxFilter === 'all') return list;
    return list.filter((t) => t.category === taxFilter);
  }, [taxesData.taxes, taxFilter]);

  const loadTaxesData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const apiRows = await getAdditionalTaxes().catch(() => []);
      setTaxesData((prev) => mergeTaxesData({ ...DEFAULT_TAXES_DATA, ...prev }, apiRows));
    } catch (error) {
      logger.error('Error loading additional taxes:', error);
      setLoadError(p('load_error'));
      setTaxesData(DEFAULT_TAXES_DATA);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchCurrentFiscalYearContext()
      .then(({ label }) => {
        if (label) {
          setFiscalYearLabel(label);
          setTaxesData((prev) => ({
            ...prev,
            overview: { ...prev.overview, exercice: prev.overview?.exercice || label },
          }));
        }
      })
      .catch(() => {});
    loadTaxesData();
  }, [loadTaxesData]);

  const openTaxDetail = (tax) => {
    setSelectedTax(tax);
    setShowTaxDetailModal(true);
  };

  const handleCalculIRA = async () => {
    const base = parseFloat(String(iraSalaire).replace(/\s/g, '').replace(',', '.'));
    if (!base || base <= 0) return;
    setIraCalculating(true);
    try {
      const result = await calculateAdditionalTax('ira', base);
      setIraResult(result);
    } catch (error) {
      logger.error('IRA calculation error:', error);
      setIraResult({ amount: base * 0.1, calculation_details: { formula: `${formatAmount(base)} × 10%`, result: base * 0.1 } });
    }
    setIraCalculating(false);
  };

  const handleExportReport = async () => {
    let rows = [];
    const taxes = taxesData.taxes || [];
    if (reportType === 'all') {
      rows = taxes.map((t) => ({
        [p('export_col_code')]: t.code,
        [p('export_col_tax')]: t.nom,
        [p('export_col_deadline')]: t.deadline,
        [p('export_col_amount')]: t.montantDu,
        [p('export_col_status')]: getStatusLabel(t.status),
      }));
    } else {
      const tax = taxes.find((t) => t.category === reportType || t.id === reportType);
      if (tax) {
        rows = [{ [p('export_row_item')]: tax.nom, [p('export_row_taxable_base')]: tax.baseImposable, [p('export_row_amount_due')]: tax.montantDu, [p('export_row_rate')]: tax.taux }];
        if (tax.details) {
          rows = [...rows, ...Object.entries(tax.details).map(([k, v]) => ({ [p('export_row_item')]: k, [p('export_row_value')]: v }))];
        }
      }
    }
    await generateReportPDF(p('fiscal_report_pdf_title'), rows, { orientation: 'landscape' });
    setShowRapportFiscal(false);
  };

  const handleExportTax = async (tax) => {
    const rows = [
      { [p('export_row_taxable_base')]: tax.baseImposable },
      { [p('export_row_amount_due')]: tax.montantDu },
      { [p('export_row_rate')]: tax.taux },
      { [p('export_row_deadline')]: tax.deadline },
      ...(tax.reductions || []).map((r) => ({ [p('export_row_item')]: r.libelle, [p('export_row_amount')]: -r.montant })),
    ];
    await generateReportPDF(p('export_declaration_title', { name: tax.nom }), rows);
  };

  const statsCards = useMemo(() => [
    { titleKey: 'stat_taxes_managed', value: taxesData.overview.totalTaxes, icon: Calculator, color: 'from-amber-500 to-orange-600' },
    { titleKey: 'stat_declarations_ok', value: taxesData.overview.completedTaxes, icon: CheckCircle, color: 'from-green-500 to-emerald-600' },
    { titleKey: 'stat_amount_due', value: `${formatAmount(taxesData.overview.pendingAmount)} ${getBrandCurrencySuffix()}`, icon: AlertTriangle, color: 'from-red-500 to-pink-600' },
    { titleKey: 'stat_next_deadline', value: taxesData.overview.nextDeadline || '—', icon: Clock, color: 'from-blue-500 to-cyan-600' },
    { titleKey: 'stat_remaining_due', value: `${formatAmount(taxesData.summary?.restantDu)} ${getBrandCurrencySuffix()}`, icon: Banknote, color: 'from-purple-500 to-pink-600' },
  ], [t, taxesData]);

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
  } = useComptabiliteTableColumnVisibility(CALENDAR_LIST_COLUMNS, p);

  const renderTaxCard = (tax) => (
    <div key={tax.id} className="relative group">
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${
        tax.status === 'completed' ? 'from-green-500 to-emerald-600'
          : tax.status === 'in_progress' ? 'from-orange-500 to-amber-600'
            : 'from-red-500 to-pink-600'
      } rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
      <button
        type="button"
        onClick={() => openTaxDetail(tax)}
        className={`relative w-full text-left bg-white/10 backdrop-blur-lg rounded-3xl border p-6 transition-all duration-300 hover:border-white/40 ${
          selectedTax?.id === tax.id ? 'border-amber-400/60 ring-2 ring-amber-500/30' : 'border-white/20'
        }`}
      >
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm ${
              tax.status === 'completed' ? 'bg-green-500' : tax.status === 'in_progress' ? 'bg-orange-500' : 'bg-red-500'
            }`}>
              {tax.code}
            </div>
            <div className={`p-1 rounded-full ${
              tax.status === 'completed' ? 'bg-green-500/20 text-green-300'
                : tax.status === 'in_progress' ? 'bg-orange-500/20 text-orange-300'
                  : 'bg-red-500/20 text-red-300'
            }`}>
              {tax.status === 'completed' ? <CheckCircle size={16} /> : tax.status === 'in_progress' ? <RefreshCw size={16} /> : <Clock size={16} />}
            </div>
          </div>
          <div className="text-right">
            <span className="text-white/70 text-xs">{tax.type || tax.code}</span>
            <p className="text-amber-400 font-semibold text-sm">{tax.taux}</p>
          </div>
        </div>
        <h4 className="text-white font-semibold text-base">{tax.nom}</h4>
        <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/10">
          <span className="text-white/70 text-sm">{p('label_deadline')}: <span className="text-orange-300 font-semibold">{tax.deadline}</span></span>
          <span className="text-emerald-400 font-bold">{formatAmount(tax.montantDu)} ${getBrandCurrencySuffix()}</span>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
          <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
            tax.status === 'completed' ? 'bg-green-500/20 text-green-300'
              : tax.status === 'in_progress' ? 'bg-orange-500/20 text-orange-300'
                : 'bg-red-500/20 text-red-300'
          }`}>
            {getStatusLabel(tax.status)}
          </span>
          <span className="text-amber-400 text-sm font-medium">{p('label_details')} →</span>
        </div>
      </button>
    </div>
  );

  const renderCalendarTable = (compact = false) => (
    <div className="glass-table-scroll overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            {isColVisible('date') ? (
              <th className="px-4 py-3 text-left text-white/80 text-sm font-medium">{p('col_date')}</th>
            ) : null}
            {isColVisible('tax') ? (
              <th className="px-4 py-3 text-left text-white/80 text-sm font-medium">{p('col_tax')}</th>
            ) : null}
            {isColVisible('amount') ? (
              <th className="px-4 py-3 text-right text-white/80 text-sm font-medium">{p('col_amount')}</th>
            ) : null}
            {isColVisible('status') ? (
              <th className="px-4 py-3 text-center text-white/80 text-sm font-medium">{p('col_status')}</th>
            ) : null}
            {!compact ? (
              <QawaemTableHeaderActionsCell
                label={p('col_actions')}
                columns={qawaemColumnDefs}
                onToggle={handleToggleColumn}
                visibleCount={qawaemVisibleColumnCount}
                totalCount={qawaemColumnDefs.length}
                thClassName="px-4 py-3 text-center text-white/80 text-sm font-medium"
              />
            ) : null}
          </tr>
        </thead>
        <tbody>
          {(taxesData.calendar || []).map((item, index) => {
            const recordId = item.taxId ?? `cal-${index}`;
            const itemStatus = calendarStatusOverrides[recordId] ?? item.status;
            return (
              <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                {isColVisible('date') ? (
                <td className="px-4 py-3 text-blue-400 font-mono text-sm">{item.date}</td>
                ) : null}
                {isColVisible('tax') ? (
                <td className="px-4 py-3 text-white font-medium">{item.taxe}</td>
                ) : null}
                {isColVisible('amount') ? (
                <td className="px-4 py-3 text-right text-emerald-400 font-semibold">{formatAmount(item.montant)} ${getBrandCurrencySuffix()}</td>
                ) : null}
                {isColVisible('status') ? (
                <td className="px-4 py-3 text-center">
                  <QawaemClickableStatusBadge
                    status={itemStatus}
                    recordId={recordId}
                    options={calendarStatusBadgeOptions}
                    onChange={handleCalendarStatusChange}
                    title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                  />
                </td>
                ) : null}
                {!compact && (
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {itemStatus !== 'completed' && (
                        <button type="button" onClick={() => setShowTeledeclarer(true)} className="p-1.5 text-green-400 hover:text-green-300 transition-colors" title={p('tooltip_teledeclare')}>
                          <Send size={14} />
                        </button>
                      )}
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => {
                          const tax = (taxesData.taxes || []).find((t) => t.id === item.taxId);
                          if (tax) openTaxDetail(tax);
                        }}
                        viewTitle={p('tooltip_view_detail')}
                      />
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
        </div>
      );
    }

    if (activeTab === 'taxes') {
      return (
        <div className="space-y-6">
          {loadError && (
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 text-orange-200 text-sm">
              <AlertCircle size={16} />
              {loadError}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {taxFilters.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setTaxFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  taxFilter === f.id
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/30'
                    : 'bg-white/10 text-white/70 hover:bg-white/15 border border-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {filteredTaxes.length === 0 ? (
            <EmptyTabState icon={Calculator} title={p('empty_category_title')} description={p('empty_category_desc')} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {filteredTaxes.map(renderTaxCard)}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'calendrier') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-semibold text-white">{p('calendar_title', { year: taxesData.overview.exercice || fiscalYearLabel || resolveFiscalYearLabel() })}</h3>
              <p className="text-white/60 text-sm">{p('calendar_subtitle')}</p>
            </div>
            <button type="button" onClick={() => setShowPlanningFiscal(true)} className="text-amber-400 hover:text-amber-300 text-sm font-medium flex items-center gap-1">
              <Calendar size={14} />
              {p('calendar_planning')}
            </button>
          </div>
          {(taxesData.calendar || []).length === 0 ? (
            <EmptyTabState icon={Calendar} title={p('calendar_empty_title')} description={p('calendar_empty_desc')} />
          ) : (
            renderCalendarTable(false)
          )}
        </div>
      );
    }

    if (activeTab === 'synthese') {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { titleKey: 'summary_total_annual', value: taxesData.summary?.totalAnnuel, subKey: 'summary_all_taxes', color: 'from-green-500 to-emerald-600', icon: CheckCircle },
              { titleKey: 'summary_paid', value: taxesData.summary?.verse, subKey: 'summary_payments_made', color: 'from-blue-500 to-cyan-600', icon: Banknote },
              { titleKey: 'summary_remaining', value: taxesData.summary?.restantDu, subKey: 'summary_due_soon', color: 'from-orange-500 to-red-600', icon: AlertTriangle },
            ].map((card, i) => (
              <div key={i} className="relative group">
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${card.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`p-2 rounded-xl bg-gradient-to-r ${card.color}`}>
                      <card.icon className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="text-white/80 text-sm font-semibold">{p(card.titleKey)}</h4>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {card.value >= 1000000 ? `${(card.value / 1000000).toFixed(2)}M ${getBrandCurrencySuffix()}` : formatAmount(card.value)}
                  </p>
                  <p className="text-white/50 text-sm mt-1">{p(card.subKey)}</p>
                </div>
              </div>
            ))}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{p('quick_actions')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { labelKey: 'action_ira_calculator', icon: Calculator, color: 'from-amber-600 to-orange-600', action: () => setShowCalculIRA(true) },
                { labelKey: 'action_fiscal_report', icon: FileSpreadsheet, color: 'from-blue-600 to-cyan-600', action: () => setShowRapportFiscal(true) },
                { labelKey: 'action_zatca_submit', icon: Send, color: 'from-green-600 to-emerald-600', action: () => setShowTeledeclarationsZATCA(true) },
                { labelKey: 'action_fiscal_planning', icon: Calendar, color: 'from-purple-600 to-pink-600', action: () => setShowPlanningFiscal(true) },
              ].map((btn, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={btn.action}
                  className={`bg-gradient-to-r ${btn.color} text-white p-4 rounded-xl font-medium hover:opacity-90 transition-all flex items-center space-x-3 shadow-lg`}
                >
                  <btn.icon className="h-5 w-5 shrink-0" />
                  <span className="text-sm">{p(btn.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const portalShell = (open, onClose, title, Icon, iconClass, children, footer) => open && ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '960px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            {Icon && <Icon className={`h-6 w-6 ${iconClass}`} />}
            {title}
          </h2>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
        {footer && <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap">{footer}</div>}
      </motion.div>
    </div>,
    document.body
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">{p('title')}</h2>
          <p className="text-white/60 text-sm">
            {p('subtitle')} — {taxesData.overview.exercice || fiscalYearLabel || resolveFiscalYearLabel()}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setShowEcheancier(true)}
            className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-2 rounded-xl font-medium hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
          >
            <Calendar size={16} />
            <span>{p('btn_echeancier')}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowTeledeclarer(true)}
            className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-4 py-2 rounded-xl font-medium hover:from-amber-700 hover:to-orange-700 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-amber-500/30"
          >
            <Send size={16} />
            <span>{p('btn_teledeclare')}</span>
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.slice(0, 4).map((stat, i) => (
            <div key={i} className="relative group">
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
              <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-5 border border-white/20">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-white/60 text-xs font-medium truncate">{p(stat.titleKey)}</h3>
                    <p className="text-lg font-bold text-white mt-1 truncate">{stat.value}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl bg-gradient-to-r ${stat.color} shrink-0`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border-b border-white/10 m-2 mb-0">
            <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/30'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>

      {/* Tax Detail Modal */}
      {showTaxDetailModal && selectedTax && portalShell(
        showTaxDetailModal,
        () => setShowTaxDetailModal(false),
        selectedTax.nom,
        Calculator,
        'text-amber-400',
        (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="bg-amber-500 text-white w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold">{selectedTax.code}</span>
            </div>
            <div className="bg-white/5 rounded-xl border border-white/20 p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><p className="text-white/60 text-sm">{p('detail_type')}</p><p className="text-white font-semibold">{selectedTax.type || selectedTax.code}</p></div>
              <div><p className="text-white/60 text-sm">{p('detail_rate')}</p><p className="text-amber-400 font-semibold">{selectedTax.taux}</p></div>
              <div><p className="text-white/60 text-sm">{p('detail_deadline')}</p><p className="text-orange-400 font-semibold">{selectedTax.deadline}</p></div>
              <div><p className="text-white/60 text-sm">{p('detail_taxable_base')}</p><p className="text-emerald-400 font-bold">{formatAmount(selectedTax.baseImposable)} ${getBrandCurrencySuffix()}</p></div>
              <div><p className="text-white/60 text-sm">{p('detail_amount_due')}</p><p className="text-green-400 font-bold text-xl">{formatAmount(selectedTax.montantDu)} ${getBrandCurrencySuffix()}</p></div>
            </div>
            {selectedTax.details && Object.keys(selectedTax.details).length > 0 && (
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <div className="p-4 border-b border-white/10"><h4 className="text-white font-semibold">{p('detail_calculations_title')}</h4></div>
                <div className="p-4 space-y-2">
                  {Object.entries(selectedTax.details).map(([key, value]) => (
                    <div key={key} className="flex justify-between bg-white/5 rounded-lg p-3">
                      <span className="text-white/70 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                      <span className="text-emerald-400 font-semibold">
                        {typeof value === 'number' && value > 100 ? `${formatAmount(value)} ${getBrandCurrencySuffix()}` : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ),
        <>
          <button type="button" onClick={() => setShowTaxDetailModal(false)} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all font-medium">{t('common.close')}</button>
          <button type="button" onClick={() => handleExportTax(selectedTax)} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg flex items-center gap-2">
            <Download size={16} />
            {p('export_declaration_btn')}
          </button>
        </>
      )}

      {portalShell(
        showEcheancier,
        () => setShowEcheancier(false),
        p('btn_echeancier'),
        Calendar,
        'text-blue-400',
        (
          <div className="space-y-4">
            <p className="text-white/60 text-sm">{p('calendar_subtitle')}</p>
            {renderCalendarTable(true)}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-blue-300 text-sm"><strong>{p('total_remaining_label')}</strong> {formatAmount(taxesData.summary?.restantDu)} ${getBrandCurrencySuffix()}</p>
            </div>
          </div>
        ),
        <button type="button" onClick={() => { setShowEcheancier(false); setActiveTab('calendrier'); }} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-semibold flex items-center gap-2">
          <Eye size={16} />
          {p('view_calendar_tab_btn')}
        </button>
      )}

      {portalShell(
        showTeledeclarer,
        () => setShowTeledeclarer(false),
        p('teledeclare_modal_title'),
        Send,
        'text-green-400',
        (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-300 text-sm"><strong>ملاحظة:</strong> سجّل الدخول إلى بوابة ZATCA لتقديم الإقرارات والمدفوعات إلكترونيًا.</p>
            </div>
            <div className="space-y-3">
              {(taxesData.taxes || []).filter((t) => t.status !== 'completed').map((tax) => (
                <div key={tax.id} className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-white font-medium">{tax.nom}</div>
                    <div className="text-white/60 text-sm">{p('deadline_amount_line', { deadline: tax.deadline, amount: formatAmount(tax.montantDu), currency: getBrandCurrencySuffix() })}</div>
                  </div>
                  <button type="button" onClick={() => openTaxDetail(tax)} className="bg-green-500/20 border border-green-500/30 text-green-200 px-4 py-2 rounded-xl hover:bg-green-500/30 transition-colors flex items-center gap-2 text-sm">
                    <Send size={14} />
                    {p('prepare_declaration_btn')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ),
        <button type="button" onClick={() => { setShowTeledeclarer(false); setShowTeledeclarationsZATCA(true); }} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold flex items-center gap-2">
          <Send size={16} />
          {p('zatca_portal_btn')}
        </button>
      )}

      {portalShell(
        showCalculIRA,
        () => { setShowCalculIRA(false); setIraResult(null); },
        'حاسبة ضريبة الاستقطاع (WHT)',
        Calculator,
        'text-amber-400',
        (
          <div className="space-y-6">
            <div className="bg-white/5 rounded-xl border border-white/20 p-6 space-y-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('ira_gross_salary_label')}</label>
                <CustomNumberInput value={iraSalaire} onChange={setIraSalaire} placeholder="0" suffixInText={` ${getBrandCurrencySuffix()}`} className="w-full" />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('ira_category_label')}</label>
                <CustomSelect value={iraCategorie} onChange={setIraCategorie} options={IRA_CATEGORIES} autoSelect={false} />
              </div>
              <button
                type="button"
                onClick={handleCalculIRA}
                disabled={iraCalculating}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-xl font-semibold hover:from-amber-700 hover:to-orange-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {iraCalculating ? <RefreshCw size={20} className="animate-spin" /> : <Calculator size={20} />}
                {p('calculate_wht_btn')}
              </button>
            </div>
            {iraResult && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <h4 className="text-green-300 font-semibold mb-2">{p('result_title')}</h4>
                <p className="text-2xl font-bold text-white">{formatAmount(iraResult.amount)} ${getBrandCurrencySuffix()}</p>
                <p className="text-white/60 text-sm mt-1">
                  {iraResult.calculation_details?.formula || p('ira_formula_fallback')}
                </p>
              </div>
            )}
          </div>
        ),
        null
      )}

      {portalShell(
        showRapportFiscal,
        () => setShowRapportFiscal(false),
        p('fiscal_report_modal_title'),
        FileSpreadsheet,
        'text-blue-400',
        (
          <div className="space-y-4">
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('report_type_label')}</label>
              <CustomSelect value={reportType} onChange={setReportType} options={REPORT_TYPES} autoSelect={false} />
            </div>
            <button type="button" onClick={handleExportReport} className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all flex items-center justify-center gap-2">
              <Download size={20} />
              {p('download_pdf_btn')}
            </button>
          </div>
        ),
        null
      )}

      {portalShell(
        showTeledeclarationsZATCA,
        () => setShowTeledeclarationsZATCA(false),
        'بوابة ZATCA — التقديم الإلكتروني',
        Send,
        'text-green-400',
        (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-300 text-sm"><strong>بوابة ZATCA:</strong> الوصول إلى البوابة الرسمية للإقرارات والمدفوعات الإلكترونية.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { title: 'إقرار ضريبة القيمة المضافة (VAT)', sub: 'تقديم شهري/ربع سنوي — 15%' },
                { title: 'إقرار الزكاة', sub: 'تقديم سنوي — 2.5%' },
                { title: 'ضريبة الاستقطاع (WHT)', sub: 'مدفوعات غير المقيمين' },
                { title: 'ضريبة الدخل (CIT)', sub: 'للمساهمين غير السعوديين/الخليجيين' },
                { title: 'الدفع الإلكتروني', sub: 'سداد الضرائب عبر ZATCA' },
                { title: 'متابعة الملف الضريبي', sub: 'عرض حالة الإقرارات المقدمة' },
              ].map((item, i) => (
                <div key={i} className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 blur-sm" style={{ pointerEvents: 'none' }} />
                  <button type="button" className="relative w-full bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 text-left hover:border-white/40 transition-all">
                    <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                    <p className="text-white/60 text-sm mb-4">{item.sub}</p>
                    <span className="text-green-400 text-sm flex items-center"><Send size={14} className="mr-2" />الدخول إلى البوابة</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ),
        null
      )}

      {portalShell(
        showPlanningFiscal,
        () => setShowPlanningFiscal(false),
        p('planning_fiscal_title', { year: fiscalYearLabel || taxesData.overview.exercice || resolveFiscalYearLabel() }),
        Calendar,
        'text-purple-400',
        (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {fiscalMonths.map(({ key, num }) => {
              const mois = p(key);
              const monthEvents = (taxesData.calendar || []).filter((c) => {
                const parts = c.date.split('/');
                return parts.length >= 2 && parseInt(parts[1], 10) === num;
              });
              return (
                <div key={key} className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h4 className="text-white font-medium mb-2">{mois} {fiscalYearLabel || taxesData.overview.exercice || resolveFiscalYearLabel()}</h4>
                  {monthEvents.length === 0 ? (
                    <p className="text-white/40 text-sm">{p('calendar_no_deadline')}</p>
                  ) : (
                    monthEvents.map((ev, i) => (
                      <div key={i} className="flex justify-between items-center text-sm py-1">
                        <span className="text-white/70">{ev.taxe}</span>
                        <span className="text-blue-400">{ev.date.split('/')[0]} {mois}</span>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        ),
        <button type="button" onClick={() => { setShowPlanningFiscal(false); setActiveTab('calendrier'); }} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold flex items-center gap-2">
          <Calendar size={16} />
          {p('view_calendar_btn')}
        </button>
      )}
    </div>
  );
};

export default TaxesSupplementairesPage;
